import {AlertIcon, ChevronDownIcon, ChevronRightIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionMenu, AnchoredOverlay, Button, Heading, IconButton, Text} from '@primer/react'
import {clsx} from 'clsx'
import {
  type ComponentProps,
  type HTMLAttributes,
  type KeyboardEventHandler,
  memo,
  type MouseEvent,
  type Ref,
  useId,
  useRef,
} from 'react'

import hiddenUnicodeStyles from '../blob/Banners/HiddenUnicode.module.css'
import lineNumberStyles from '../blob/BlobContent/Code/LineNumber.module.css'
import {CodeLineMenu, type CodeLineMenuAction} from '../blob/BlobContent/CodeLineMenu'
import styles from './TaillightsGutterRow.module.css'

export type TaillightsGutterAction = CodeLineMenuAction

export type TaillightsGutterAnnotation = {
  kind: 'unicode' | 'codeowners'
  message: string
}

export interface TaillightsGutterRowProps {
  /** Original, 1-based source identity; never the projected row index. */
  lineNumber: number
  top: number
  /** A 1-based row in the host grid; omitting it retains the standalone row wrapper. */
  gridRow?: number
  embedded?: boolean
  /** Must match the fixed 20px source row pitch; other heights are rejected, not clamped. */
  height: number
  edge?: 'start' | 'end'
  selected?: boolean
  /** Paints the row as part of a highlighted range without marking it as a selected line. */
  highlighted?: boolean
  rangeStart?: boolean
  /** Only one projected row should participate in sequential keyboard navigation. */
  tabStop?: boolean
  lane: 0 | 1
  fold?: {startLine: number; collapsed: boolean; disabled?: boolean}
  annotations?: readonly TaillightsGutterAnnotation[]
  menuOpen: boolean
  showLineActions?: boolean
  annotationOpen?: boolean
  actions: readonly TaillightsGutterAction[]
  menuLabel?: string
  ref?: Ref<HTMLAnchorElement>
  onLine: (lineNumber: number, extend: boolean) => void
  onToggle: (startLine: number) => void
  onSnapshot: () => void
  onMenuOpenChange: (lineNumber: number, open: boolean, closeReason?: 'tab') => void
  onAnnotationOpenChange: (lineNumber: number, open: boolean) => void
  onFocusChange: (lineNumber: number | null) => void
}

type GutterControlProps = Pick<
  HTMLAttributes<HTMLElement>,
  'onPointerDownCapture' | 'onKeyDownCapture' | 'onFocus' | 'onBlur'
> & {'data-gutter-line': number; 'data-gutter-owner'?: string}

function isModified(event: MouseEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native link navigation, not a shortcut.
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey
}

function MenuAnchor({tabStop, ...props}: ComponentProps<typeof IconButton> & {tabStop: boolean}) {
  // AnchoredOverlay supplies tabIndex=0; the adapter, not each mounted row, owns the tab stop.
  return <IconButton {...props} tabIndex={tabStop ? 0 : -1} />
}

/** Renders a 20px source-line gutter with caller-controlled selection, folding, actions and annotation details. */
export const TaillightsGutterRow = memo(function GutterRow({
  lineNumber,
  top,
  gridRow,
  embedded = false,
  height,
  edge,
  selected = false,
  highlighted = false,
  rangeStart = false,
  tabStop = false,
  lane,
  fold,
  annotations,
  menuOpen,
  showLineActions = true,
  annotationOpen = false,
  actions,
  menuLabel,
  ref,
  onLine,
  onToggle,
  onSnapshot,
  onMenuOpenChange,
  onAnnotationOpenChange,
  onFocusChange,
}: TaillightsGutterRowProps) {
  if (height !== 20) throw new RangeError('TaillightsGutterRow requires a 20px source row height')
  if (gridRow !== undefined && (!Number.isInteger(gridRow) || gridRow < 1)) {
    throw new RangeError('TaillightsGutterRow requires a positive integer grid row')
  }

  const stateId = useId()
  const tabIndex = tabStop ? 0 : -1
  // Empty rows keep the host's action-strip layout, but do not render a menu.
  const hasLineActions = showLineActions && actions.length > 0
  const interactionProps: GutterControlProps = {
    'data-gutter-line': lineNumber,
    'data-gutter-owner': stateId,
    onPointerDownCapture: onSnapshot,
    onKeyDownCapture: onSnapshot,
    onFocus: () => onFocusChange(lineNumber),
    onBlur: event => {
      if (event.currentTarget.contains(event.relatedTarget)) return
      const next = event.relatedTarget instanceof Element ? event.relatedTarget.closest('[data-gutter-line]') : null
      if (gridRow !== undefined && next?.getAttribute('data-gutter-owner') === stateId) {
        return
      }
      onFocusChange(null)
    },
  }
  const controlProps = gridRow === undefined ? undefined : interactionProps
  const gridStyle = {
    gridRow: gridRow === undefined ? undefined : `${gridRow} / span 1`,
    '--line-number-digits': String(lineNumber).length,
  }
  const menu = hasLineActions && (
    <TaillightsGutterMenu
      lineNumber={lineNumber}
      lane={lane}
      tabStop={tabStop}
      menuOpen={menuOpen}
      actions={actions}
      menuLabel={menuLabel}
      onMenuOpenChange={onMenuOpenChange}
      gridRow={gridRow}
      edge={edge}
      controlProps={controlProps}
      embedded={embedded}
    />
  )
  const annotation = !!annotations?.length && (
    <GutterAnnotations
      lineNumber={lineNumber}
      tabStop={tabStop}
      annotations={annotations}
      annotationOpen={annotationOpen}
      onAnnotationOpenChange={onAnnotationOpenChange}
      gridRow={gridRow}
      showLineActions={showLineActions}
      controlProps={controlProps}
      embedded={embedded}
    />
  )
  const content = (
    <>
      {embedded && annotation && (
        <span className={styles.EmbeddedAnnotationAnchor} style={gridStyle}>
          <span className={clsx(lineNumberStyles.codeAlert, lineNumberStyles.codeAlertCentered)}>{annotation}</span>
        </span>
      )}
      {!embedded && (
        <>
          {menu}
          {annotation}
        </>
      )}
      <a
        {...controlProps}
        ref={ref}
        className={clsx(
          styles.LineLink,
          gridRow !== undefined && styles.GridLink,
          (selected || highlighted) && styles.selected,
        )}
        style={gridStyle}
        data-line-actions={showLineActions}
        href={`#L${lineNumber}`}
        tabIndex={tabIndex}
        aria-label={`Line ${lineNumber}`}
        aria-describedby={selected ? stateId : undefined}
        onClick={event => {
          if (isModified(event)) return
          event.preventDefault()
          onLine(lineNumber, event.shiftKey)
        }}
      >
        {lineNumber}
      </a>
      {fold && (
        <span
          {...controlProps}
          className={clsx(
            styles.Fold,
            lineNumberStyles.codeAlert,
            lineNumberStyles.codeAlertRight,
            gridRow !== undefined && styles.GridFold,
          )}
          style={{...gridStyle, lineHeight: 'inherit', marginBlock: 0}}
        >
          <button
            type="button"
            tabIndex={tabIndex}
            className={clsx(
              lineNumberStyles.codeFoldingChevron,
              styles.FoldButton,
              gridRow !== undefined && styles.GridFoldButton,
            )}
            aria-label={`${fold.collapsed ? 'Expand' : 'Collapse'} section at line ${fold.startLine}`}
            aria-expanded={!fold.collapsed}
            aria-disabled={fold.disabled || undefined}
            onClick={() => {
              if (!fold.disabled) onToggle(fold.startLine)
            }}
          >
            {fold.collapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
          </button>
        </span>
      )}
      {selected && (
        <span id={stateId} className={styles.SrOnly}>
          {rangeStart ? 'Start of selected range' : 'Selected line'}
        </span>
      )}
    </>
  )
  if (gridRow !== undefined) return content
  return (
    <div
      {...interactionProps}
      className={clsx(styles.Row, (selected || highlighted) && styles.selected)}
      data-edge={edge}
      data-line-actions={showLineActions}
      style={{top, height, lineHeight: `${height}px`, padding: 0, cursor: 'default'}}
    >
      {content}
    </div>
  )
})

export function TaillightsGutterMenu({
  lineNumber,
  lane,
  tabStop = false,
  menuOpen,
  actions,
  menuLabel: providedMenuLabel,
  onMenuOpenChange,
  gridRow,
  edge,
  controlProps,
  embedded,
}: Pick<
  TaillightsGutterRowProps,
  'lineNumber' | 'lane' | 'tabStop' | 'menuOpen' | 'actions' | 'menuLabel' | 'onMenuOpenChange' | 'gridRow' | 'edge'
> & {
  controlProps?: GutterControlProps
  embedded: boolean
}) {
  const menuRef = useRef<HTMLButtonElement>(null)
  const tabEventRef = useRef<KeyboardEvent | null>(null)
  const captureKeyDown: KeyboardEventHandler<HTMLElement> = event => {
    controlProps?.onKeyDownCapture?.(event)
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Observe Primer's Tab dismissal without handling or preventing the key.
    tabEventRef.current = menuOpen && event.key === 'Tab' ? event.nativeEvent : null
  }
  const menuLabel = providedMenuLabel === undefined ? `Line ${lineNumber} actions` : providedMenuLabel
  return (
    <ActionMenu
      open={menuOpen}
      anchorRef={menuRef}
      onOpenChange={open => {
        const tabEvent = tabEventRef.current
        // Primer exposes only a boolean. An active Tab event distinguishes navigation from later dismissals.
        const closeReason = !open && tabEvent && tabEvent.eventPhase !== Event.NONE ? 'tab' : undefined
        tabEventRef.current = null
        onMenuOpenChange(lineNumber, open, closeReason)
      }}
    >
      <ActionMenu.Anchor>
        <MenuAnchor
          {...controlProps}
          onKeyDownCapture={captureKeyDown}
          icon={KebabHorizontalIcon}
          aria-label={`Line ${lineNumber} actions`}
          variant={embedded ? 'default' : 'invisible'}
          size="small"
          tabStop={tabStop}
          className={
            embedded
              ? styles.EmbeddedActionButton
              : clsx(
                  styles.Target,
                  styles.Actions,
                  lane === 1 && styles.secondLane,
                  gridRow !== undefined && styles.GridControl,
                )
          }
          style={embedded || gridRow === undefined ? undefined : {gridRow: `${gridRow} / span 1`}}
          data-edge={edge}
        />
      </ActionMenu.Anchor>
      <CodeLineMenu
        actions={actions}
        menuLabel={menuLabel}
        heading={embedded ? undefined : menuLabel}
        overlayProps={{
          ...controlProps,
          onKeyDownCapture: captureKeyDown,
          ...(!embedded && {className: styles.Menu, width: undefined}),
        }}
      />
    </ActionMenu>
  )
}

function GutterAnnotations({
  lineNumber,
  tabStop,
  annotations,
  annotationOpen = false,
  onAnnotationOpenChange,
  gridRow,
  showLineActions,
  controlProps,
  embedded,
}: Pick<
  TaillightsGutterRowProps,
  'lineNumber' | 'tabStop' | 'annotationOpen' | 'onAnnotationOpenChange' | 'gridRow' | 'showLineActions'
> & {
  annotations: readonly TaillightsGutterAnnotation[]
  controlProps?: GutterControlProps
  embedded: boolean
}) {
  const annotationRef = useRef<HTMLButtonElement>(null)
  const annotationId = useId()
  return (
    <AnchoredOverlay
      open={annotationOpen}
      anchorRef={annotationRef}
      onOpen={() => onAnnotationOpenChange(lineNumber, true)}
      onClose={() => onAnnotationOpenChange(lineNumber, false)}
      renderAnchor={props => (
        <IconButton
          {...props}
          {...controlProps}
          icon={AlertIcon}
          aria-haspopup="dialog"
          aria-label={`Line ${lineNumber} annotations: ${annotations.map(item => (item.kind === 'unicode' ? 'Hidden Unicode' : 'CODEOWNERS error')).join(', ')}`}
          variant="invisible"
          size="small"
          tabIndex={tabStop ? 0 : -1}
          className={
            embedded
              ? clsx(hiddenUnicodeStyles.Octicon, styles.EmbeddedAnnotation)
              : clsx(styles.Target, styles.Annotation, gridRow !== undefined && styles.GridControl)
          }
          style={embedded || gridRow === undefined ? undefined : {gridRow: `${gridRow} / span 1`}}
          data-line-actions={showLineActions}
        />
      )}
      overlayProps={{...controlProps, role: 'dialog', 'aria-labelledby': annotationId}}
      className={styles.Details}
      preventOverflow={false}
    >
      <Heading as="h3" id={annotationId} className={styles.DetailsHeading}>
        Line {lineNumber} annotations
      </Heading>
      {annotations.map(item => (
        <Text as="p" key={item.kind}>
          <strong>{item.kind === 'unicode' ? 'Hidden Unicode' : 'CODEOWNERS error'}: </strong>
          {item.message}
        </Text>
      ))}
      <Button onClick={() => onAnnotationOpenChange(lineNumber, false)}>Close annotations</Button>
    </AnchoredOverlay>
  )
}
